import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Calendar, Route as RouteIcon, Users, Image as ImageIcon, MapPin, Sparkles, AlertTriangle, Share2, Youtube, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useTrailPanelData } from "@/hooks/useTrailPanelData";
import { useTrailDescription } from "@/hooks/useTrailDescription";
import { useIsMobile } from "@/hooks/use-mobile";
import TrailDescriptionFeedback from "@/components/TrailDescriptionFeedback";
import { TrailPreferenceButtons } from "@/components/TrailPreferenceButtons";
import { formatDistanceToNow } from "date-fns";
import { ROUTES } from "@/lib/routes";
import { toast } from "sonner";
import YouTubePlayerDialog from "@/components/YouTubePlayerDialog";

interface TrailDetailPanelProps {
  trailId: string | null;
  open: boolean;
  onClose: () => void;
}

const getDifficultyVariant = (difficulty?: string) => {
  switch (difficulty) {
    case 'easy': return 'default';
    case 'moderate': return 'secondary';
    case 'hard': return 'destructive';
    case 'extreme': return 'destructive';
    default: return 'outline';
  }
};

const truncateDescription = (text: string, maxLength = 300): string => {
  if (text.length <= maxLength) return text;
  const truncated = text.substring(0, maxLength);
  const lastSentence = truncated.lastIndexOf('.');
  if (lastSentence > maxLength * 0.6) {
    return truncated.substring(0, lastSentence + 1);
  }
  return truncated.trim() + '...';
};

const formatDifficulties = (difficulties: string[]): string | null => {
  if (!difficulties || difficulties.length === 0) return null;
  if (difficulties.length === 1) return difficulties[0];
  const order = ['easy', 'moderate', 'hard', 'extreme'];
  const sorted = difficulties.sort((a, b) => 
    order.indexOf(a.toLowerCase()) - order.indexOf(b.toLowerCase())
  );
  return `${sorted[0]} to ${sorted[sorted.length - 1]}`;
};

const formatSurfaceTypes = (types: string[]): string | null => {
  if (!types || types.length === 0) return null;
  if (types.length === 1) return types[0];
  return types.join(' / ');
};

const formatLocation = (forests: string[]): string | null => {
  if (forests && forests.length > 0) {
    return forests[0];
  }
  return null;
};

const PanelContent = ({ trailId, onClose }: { trailId: string; onClose: () => void }) => {
  const navigate = useNavigate();
  const { trail, stats, isLoading } = useTrailPanelData(trailId);
  const { description, isGenerating, isFlagged, generateDescription, refreshDescription } = useTrailDescription(trailId);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = React.useState(false);
  const [videoDialog, setVideoDialog] = React.useState<{ url: string; title: string } | null>(null);

  const handleViewDetails = () => {
    navigate(ROUTES.trail(trailId));
    onClose();
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/trail/${trailId}`;
    const title = `Check out ${trail?.name} on Airing Down!`;
    
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard!");
      }
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard!");
      }
    }
  };

  if (isLoading) {
    return (
      <>
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <Skeleton className="h-6 w-48" />
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="space-y-4 p-4">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      </>
    );
  }

  if (!trail) {
    return (
      <>
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <h2 className="text-lg font-semibold">Trail Details</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="p-4 text-center text-muted-foreground">
          Trail not found
        </div>
      </>
    );
  }

  return (
    <>
      {/* Header with Trail Name */}
      <div className="flex items-center justify-between p-4 border-b shrink-0">
        <h2 className="text-lg font-semibold truncate pr-2">{trail.name}</h2>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <ScrollArea className="flex-1 min-h-0">
        <div className="space-y-4 p-4">
          {/* Trail Metadata */}
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {formatDifficulties(trail.difficulties) && (
                <Badge variant={getDifficultyVariant(formatDifficulties(trail.difficulties)!)}>
                  {formatDifficulties(trail.difficulties)}
                </Badge>
              )}
              {trail.length_miles > 0 && (
                <Badge variant="outline">{trail.length_miles.toFixed(2)} mi</Badge>
              )}
              {formatSurfaceTypes(trail.surface_types) && (
                <Badge variant="secondary">{formatSurfaceTypes(trail.surface_types)}</Badge>
              )}
            </div>
            {formatLocation(trail.forests) && (
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {formatLocation(trail.forests)}
              </p>
            )}
          </div>

          {/* Hero Image Section */}
        {stats.recentRunPhoto && (
          <Card className="overflow-hidden">
            <AspectRatio ratio={16 / 9}>
              <img
                src={stats.recentRunPhoto}
                alt={`${trail.name} trail`}
                className="object-cover w-full h-full photo-signature"
              />
              {stats.totalPhotos > 1 && (
                <div className="absolute top-2 right-2 bg-background/90 backdrop-blur-sm px-2 py-1 rounded-md text-xs flex items-center gap-1">
                  <ImageIcon className="h-3 w-3" />
                  <span>{stats.totalPhotos} photos</span>
                </div>
              )}
            </AspectRatio>
          </Card>
        )}

        {/* Description Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-muted-foreground">About</h3>
            {!description ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={generateDescription}
                disabled={isGenerating}
                className="h-7 text-xs"
              >
                <Sparkles className="h-3 w-3 mr-1" />
                {isGenerating ? "Researching..." : "Research Trail"}
              </Button>
            ) : (
              <TrailDescriptionFeedback
                trailId={trailId}
                descriptionId={description.id}
                variant="compact"
              />
            )}
          </div>
          {description ? (
            <div className="space-y-2">
              {/* Flagged indicator */}
              {isFlagged && (
                <div className="flex items-start gap-2 p-3 rounded-md bg-amber-500/10 border border-amber-500/20 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-600 dark:text-amber-400">This description may contain inaccuracies</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Community feedback suggests limited information is available for this trail.
                    </p>
                  </div>
                </div>
              )}
              <p className="text-sm leading-relaxed">
                {isDescriptionExpanded 
                  ? description.description 
                  : truncateDescription(description.description)}
              </p>
              {description.description.length > 300 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                  className="h-7 text-xs px-2"
                >
                  {isDescriptionExpanded ? "Show less" : "Show more"}
                </Button>
              )}
              {/* YouTube Links */}
              {description.resource_links?.youtube && description.resource_links.youtube.length > 0 && (
                <div className="space-y-1.5 pt-1">
                  <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                    <Youtube className="h-3 w-3" />
                    Trail Videos
                  </p>
                  {description.resource_links.youtube.map((link: { url: string; title: string }, i: number) => (
                    <button
                      key={i}
                      onClick={() => setVideoDialog({ url: link.url, title: link.title })}
                      className="flex items-center gap-2 text-xs text-primary hover:underline truncate w-full text-left"
                    >
                      <Play className="h-3 w-3 shrink-0" />
                      <span className="truncate">{link.title || link.url}</span>
                    </button>
                  ))}
                </div>
              )}
              {/* YouTube Player Dialog */}
              {videoDialog && (
                <YouTubePlayerDialog
                  open={!!videoDialog}
                  onOpenChange={(open) => !open && setVideoDialog(null)}
                  url={videoDialog.url}
                  title={videoDialog.title}
                />
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">
              No trail info available yet. Click "Research Trail" to learn more.
            </p>
          )}
        </div>

        {/* Activity Metrics Section */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">Activity</h3>
          <div className="grid grid-cols-2 gap-3">
            <Card>
              <CardContent className="p-3 flex items-start gap-2">
                <Users className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <div className="text-lg font-semibold">{stats.runCount}</div>
                  <div className="text-xs text-muted-foreground">
                    {stats.runCount === 1 ? 'Run' : 'Runs'}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3 flex items-start gap-2">
                <RouteIcon className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div>
                  <div className="text-lg font-semibold">{stats.routeCount}</div>
                  <div className="text-xs text-muted-foreground">
                    {stats.routeCount === 1 ? 'Route' : 'Routes'}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {stats.lastRunDate && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>Last run {formatDistanceToNow(new Date(stats.lastRunDate), { addSuffix: true })}</span>
            </div>
          )}
          {!stats.lastRunDate && stats.runCount === 0 && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>No recent activity</span>
            </div>
          )}
        </div>

        {/* Preference Buttons */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground">Save Trail</h3>
          <TrailPreferenceButtons trailId={trailId} variant="default" />
          <Button variant="outline" size="sm" className="w-full" onClick={handleShare}>
            <Share2 className="h-4 w-4 mr-2" />
            Share Trail
          </Button>
        </div>

        {/* Call-to-Action Section */}
        <div className="space-y-2 pt-2">
          <Button onClick={handleViewDetails} className="w-full">
            View Full Details
          </Button>
        </div>
      </div>
    </ScrollArea>
    </>
  );
};

export default function TrailDetailPanel({ trailId, open, onClose }: TrailDetailPanelProps) {
  const isMobile = useIsMobile();

  if (!trailId) return null;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
        <DrawerContent className="max-h-[85vh] flex flex-col">
          <DrawerHeader className="text-left shrink-0">
            <DrawerTitle className="sr-only">Trail Details</DrawerTitle>
          </DrawerHeader>
          <div className="flex-1 h-0 overflow-y-auto" data-vaul-no-drag>
            <PanelContent trailId={trailId} onClose={onClose} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Custom positioned panel scoped to map container
  return (
    <div className={`absolute top-0 right-0 h-full w-[400px] z-40 transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
      <div className="h-full w-full pointer-events-auto bg-background border-l shadow-lg flex flex-col">
        <PanelContent trailId={trailId} onClose={onClose} />
      </div>
    </div>
  );
}
